import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AuthModal } from "./components/auth/AuthModal";
import { ProtectedRoute } from "./components/ProtectedRoute";
import AdminDashboard from "./pages/AdminDashboard";
import HeadteacherDashboard from "./pages/HeadteacherDashboard";
import TeacherDashboard from "./pages/TeacherDashboard";
import SISODashboard from "./pages/SISODashboard";
import DirectorDashboard from "./pages/DirectorDashboard";
import OtherGESStaffDashboard from "./pages/OtherGESStaffDashboard";
import DashboardSwitcher from "./pages/DashboardSwitcher";
import DocumentGenerator from "./pages/DocumentGeneratorNew";
import KnowledgeBase from "./pages/KnowledgeBase";
import Contact from "./pages/Contact";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import About from "./pages/About";
import SampleLessonViewer from "./components/SampleLessonViewer";
import NetworkStatus from "./components/NetworkStatus";
import { Toaster, toast } from "sonner";
import { 
  Menu, 
  X, 
  FileText, 
  BookOpen, 
  Shield, 
  Users, 
  Star,
  Phone,
  MessageCircle,
  MapPin,
  ChevronDown,
  Scale,
  GraduationCap,
  LogIn,
  LogOut,
  User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');

  // Check for session expiry on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionExpired = urlParams.get('session_expired');
    
    if (sessionExpired === 'true') {
      // Show toast notification
      setTimeout(() => {
        toast.warning('Your session has expired due to inactivity. Please log in again.', {
          duration: 6000,
          icon: '⏰'
        });
      }, 500);
      
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleSignIn = () => {
    setAuthModalTab('login');
    setShowAuthModal(true);
  };

  const handleSignUp = () => {
    setAuthModalTab('signup');
    setShowAuthModal(true);
  };

  // Default auth modal to signup
  const handleAuthModal = () => {
    setAuthModalTab('signup');
    setShowAuthModal(true);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-red-50">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-pink-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png"
                alt="EduLegalHub Logo"
                className="w-10 h-10 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                EduLegalHub
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 ml-8">
              <a href="/about" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">About</a>
              <Link to="/document-generator" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Get Legal Document</Link>
              <button onClick={handleAuthModal} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Manage Education Ecosystem</button>
              <Link to="/knowledge-base" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Knowledge Base</Link>
              <a href="https://quaisoft.lovable.app" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Custom Software</a>
              <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Contact</Link>
              
              {/* Auth Section */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-2 text-gray-700">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-pink-500 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-semibold">{user?.name}</div>
                      <div className="text-xs text-gray-500 capitalize">
                        {user?.role === 'admin' ? 'Administrator' : 
                         user?.role === 'sub-admin' ? 'Sub Administrator' :
                         user?.role === 'headteacher' ? 'Headteacher' :
                         user?.role === 'teacher' ? 'Teacher' : 
                         user?.role}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-1 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors font-medium"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={handleSignIn}
                    variant="ghost"
                    className="text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                  <Button
                    onClick={handleAuthModal}
                    className="bg-gradient-to-r from-blue-500 to-pink-500 text-white hover:from-blue-600 hover:to-pink-600"
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={toggleMenu}
              className="lg:hidden p-2 text-gray-600 hover:text-blue-600 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="lg:hidden mt-4 py-4 border-t border-pink-200">
              <div className="flex flex-col space-y-4">
                <a href="/about" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">About</a>
                <Link to="/document-generator" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Get Legal Document</Link>
                <button onClick={handleAuthModal} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium">Manage Education Ecosystem</button>
                <Link to="/knowledge-base" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Knowledge Base</Link>
                <a href="https://quaisoft.lovable.app" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Custom Software</a>
                <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Contact</Link>
                <div className="border-t border-gray-200 pt-4">
                  {isAuthenticated ? (
                    <>
                      <div className="mb-4 px-2">
                        <div className="text-sm font-semibold text-gray-800">{user?.name}</div>
                        <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                      </div>
                      <button 
                        onClick={handleLogout}
                        className="block w-full text-left text-red-600 hover:text-red-700 transition-colors font-medium"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={handleSignIn}
                        className="block text-gray-700 hover:text-blue-600 transition-colors font-medium mb-2"
                      >
                        Sign In
                      </button>
                      <button 
                        onClick={handleSignUp}
                        className="block text-gray-700 hover:text-blue-600 transition-colors font-medium"
                      >
                        Sign Up
                      </button>
                    </>
                  )}
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-4 md:space-y-6">
                <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 text-blue-700 border-blue-200 px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm">
                  Making legal documents writing accessible, Managing educational ecosystem
                </Badge>
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight text-gray-800">
                  Educational Accountability And Monitoring System And Legal Documents Generation
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                  Our system allows players within the Education Ecosystem to access legal documents easily even from remote, and offers effective Education Accountability And Monitoring opportunities for players within the Education Ecosystem where teachers, headteachers, SISOs, Deputy Directors in charge of supervision at the district, District office registrar, and the District Director, all having their respective dashboards, plus Other GES Staff on one hand in the public sector, and teachers, headteachers, and Proprietors(ress), on the other hand in the private sector. The system also allows all players in the public sector who are due for promotion to prepare adequately for the promotion exams with all past questions converted into live exam, after which system marks, score, and review correct and wrong answers, from 2020 to current year across all ranks. The preparation also equips candidates with other learning resource materials and tips to answering aptitude test.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 md:px-8 md:py-4 text-base md:text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  asChild
                >
                  <Link to="/document-generator" className="flex items-center justify-center">
                    <FileText className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                    Generate Legal Documents
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-pink-400 text-pink-600 hover:bg-pink-50 px-6 py-3 md:px-8 md:py-4 text-base md:text-lg font-semibold rounded-full transition-all duration-300"
                  onClick={handleAuthModal}
                >
                  <BookOpen className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                  Manage Education Ecosystem
                </Button>
              </div>

              {/* Dual Purpose Info Card */}
              <div className="mt-6 p-5 md:p-6 rounded-2xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 shadow-xl">
                <p className="text-white font-medium text-sm md:text-base leading-relaxed">
                  <span className="font-bold text-yellow-300">EduManSystem</span> is a comprehensive digital platform serving Ghana&apos;s education and legal ecosystems:
                </p>
                <ul className="mt-3 space-y-2">
                  <li className="flex items-start text-white text-sm md:text-base">
                    <span className="mr-2 text-yellow-300 font-bold">&#10003;</span>
                    Access to standard legal documents without friction.
                  </li>
                  <li className="flex items-start text-white text-sm md:text-base">
                    <span className="mr-2 text-yellow-300 font-bold">&#10003;</span>
                    Education Accountability And Monitoring System for all players in the education ecosystem.
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="relative mt-8 lg:mt-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-300/20 to-pink-300/20 rounded-3xl blur-3xl"></div>
              
              {/* Sample Lesson Note Viewer - Shows PDF preview if uploaded */}
              <div className="relative z-10 mb-4">
                <SampleLessonViewer />
              </div>
              
              {/* Hero Image */}
              <img 
                src="https://customer-assets.emergentagent.com/job_atlas-connect-6/artifacts/nijs06b2_hero.jpeg"
                alt="EduManSystem - Legal Documents and Education Monitoring"
                className="relative rounded-3xl shadow-2xl w-full h-[200px] sm:h-[250px] md:h-[300px] lg:h-[350px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="about" className="py-20 px-4 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                Two Powerful Services
              </span>
              <br />
              <span className="text-gray-800">One Unified Platform</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Whether you&apos;re seeking professional legal documentation or a comprehensive Education Accountability And Monitoring System, 
              EduManSystem provides cutting-edge innovative solutions tailored to your specific needs. 
              Experience seamless integration between legal expertise and educational excellence.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Legal Documents Service */}
            <Card className="overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-3xl transition-all duration-500 group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_learndocs-9/artifacts/rz3bdzbk_legal.jpeg"
                  alt="Legal Documents Hub"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Badge className="bg-blue-500 text-white border-0">Legal Services</Badge>
                </div>
              </div>
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold text-blue-800 flex items-center">
                  <Scale className="mr-3 h-6 w-6" />
                  Legal Documents Generation Hub
                </CardTitle>
                <p className="text-sm font-semibold text-blue-600 mt-1">Fast. Standard. Ghana-Compliant.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription className="text-gray-700 text-base leading-relaxed">
                  The Legal Documents Hub is designed for individuals, professionals, and institutions who need accurate legal documents without the complexity, cost, or delay traditionally associated with legal drafting. Instead of struggling with templates or informal documents, users simply complete guided forms, and the platform generates professionally structured legal documents aligned with Ghanaian legal standards.
                </CardDescription>
                <div className="space-y-2 py-4">
                  <p className="text-sm font-semibold text-gray-800">Common Use Cases:</p>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center space-x-2">
                      <Shield className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Affidavits and statutory declarations</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Agreements and undertakings</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Letters, notices, and formal legal correspondences</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Institutional and administrative legal documents</span>
                    </div>
                  </div>
                </div>
                <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 rounded-full" asChild>
                  <Link to="/document-generator">
                    <FileText className="mr-2 h-4 w-4" />
                    Start Creating Legal Documents
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Education Service */}
            <Card className="overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-pink-50 to-red-50 hover:shadow-3xl transition-all duration-500 group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_learndocs-9/artifacts/03pl30tt_note.jpeg"
                  alt="Education Accountability And Monitoring"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Badge className="bg-pink-500 text-white border-0">Education Services</Badge>
                </div>
              </div>
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold text-pink-800 flex items-center">
                  <GraduationCap className="mr-3 h-6 w-6" />
                  Comprehensive Educational Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription className="text-gray-700 text-base leading-relaxed">
                  Our Education Accountability And Monitoring System empowers all players within the education ecosystem — teachers, headteachers, SISOs, Deputy Directors, District Directors, and Proprietors — each with their own dashboard to manage, monitor, and ensure educational excellence across public and private sectors.
                </CardDescription>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="flex items-center space-x-2">
                    <BookOpen className="h-4 w-4 text-pink-500" />
                    <span className="text-sm text-gray-600">Weekly Lessons</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-pink-500" />
                    <span className="text-sm text-gray-600">Teacher Portal</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4 text-pink-500" />
                    <span className="text-sm text-gray-600">Headteacher Portal</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Star className="h-4 w-4 text-pink-500" />
                    <span className="text-sm text-gray-600">Quality Content</span>
                  </div>
                </div>
                <Button className="w-full bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white font-semibold py-3 rounded-full" onClick={handleAuthModal}>
                  <BookOpen className="mr-2 h-4 w-4" />
                  Access Educational Resources
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                What Our Users Say
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Hear from professionals who have transformed their workflows with EduLegalHub
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 - Legal Documents User */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                    <Scale className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-blue-800">Kwame Adu</CardTitle>
                    <CardDescription className="text-blue-600">Legal Documents User— Private Business Owner</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 italic leading-relaxed">
                  "I needed a properly structured legal document urgently, and Edulegalhub delivered exactly what I needed. 
                  The process was simple, and the document met professional standards. It saved me time and unnecessary back-and-forth."
                </p>
              </CardContent>
            </Card>

            {/* Testimonial 2 - Teacher */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-pink-50 to-white hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center">
                    <GraduationCap className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-pink-800">Sampson Kyei</CardTitle>
                    <CardDescription className="text-pink-600">Teacher</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 italic leading-relaxed">
                  &quot;As an educator, EduManSystem&apos;s Education Accountability And Monitoring System has revolutionized how we manage education delivery. 
                  The comprehensive dashboards for each role make accountability seamless. The integration across all levels from teacher to District Director makes administrative oversight effortless. Highly recommend!&quot;
                </p>
              </CardContent>
            </Card>

            {/* Testimonial 3 - Headteacher */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-white hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-red-800">Samuel Ayetey</CardTitle>
                    <CardDescription className="text-red-600">Headteacher</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 italic leading-relaxed">
                  "EduLegalHub has streamlined our school's administrative processes tremendously. The teacher 
                  dashboard integration allows me to monitor lesson planning quality and ensure curriculum compliance. 
                  The platform has improved our educational standards and teacher accountability significantly."
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo.png"
                  alt="EduLegalHub Logo"
                  className="w-12 h-12 object-contain"
                />
                <span className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-pink-400 bg-clip-text text-transparent">
                  EduLegalHub
                </span>
              </div>
              <p className="text-gray-300 leading-relaxed max-w-md">
                Empowering Ghana&apos;s education ecosystem with legal document generation and comprehensive Education Accountability And Monitoring System.
              </p>
              <div className="flex space-x-4">
                <span className="text-sm text-gray-400 mr-2">Follow Us:</span>
                <a 
                  href="https://whatsapp.com/channel/0029VaH7Lu47dmeYeQPmUJ0C" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-300 hover:text-green-400 transition-colors"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span className="text-sm">WhatsApp Channel</span>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white">Quick Links</h3>
              <div className="space-y-3">
                <a href="/about" className="block text-gray-300 hover:text-blue-400 transition-colors">About</a>
                <Link to="/document-generator" className="block text-gray-300 hover:text-blue-400 transition-colors">Get Legal Document</Link>
                <button onClick={handleAuthModal} className="block text-left text-gray-300 hover:text-blue-400 transition-colors">Manage Education Ecosystem</button>
                <Link to="/knowledge-base" className="block text-gray-300 hover:text-blue-400 transition-colors">Knowledge Base</Link>
                <a href="https://quaisoft.lovable.app" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-blue-400 transition-colors">Custom Software</a>
                <Link to="/contact" className="block text-gray-300 hover:text-blue-400 transition-colors">Contact</Link>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white">Contact Info</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-blue-400" />
                  <span className="text-gray-300">+233 24 366 6428</span>
                </div>
                <a href="https://wa.me/233243666428" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-green-400 transition-colors">
                  <MessageCircle className="h-5 w-5 text-green-400" />
                  <span className="text-gray-300 hover:text-green-400">Chat on WhatsApp</span>
                </a>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-blue-400" />
                  <span className="text-gray-300">Accra, Ghana</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-700 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                © 2024 EduLegalHub. All rights reserved. | Developed by: <a href="https://quaisoft.lovable.app" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors">quaiSoft IT Solutions Ltd</a>
              </div>
              <div className="flex space-x-6 text-sm">
                <Link to="/terms" className="text-gray-400 hover:text-blue-400 transition-colors">Terms & Conditions</Link>
                <Link to="/privacy" className="text-gray-400 hover:text-blue-400 transition-colors">Privacy Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab={authModalTab}
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin" element={
              <ProtectedRoute requireAuth={true} requiredRoles={['admin', 'sub-admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/siso-dashboard" element={
              <ProtectedRoute requireAuth={true} requiredRoles={['siso']}>
                <SISODashboard />
              </ProtectedRoute>
            } />
            <Route path="/director-dashboard" element={
              <ProtectedRoute requireAuth={true} requiredRoles={['director', 'dd_supervision', 'secretary', 'hrm', 'personnel', 'supervision_team']}>
                <DirectorDashboard />
              </ProtectedRoute>
            } />
            <Route path="/ges-staff-dashboard" element={
              <ProtectedRoute requireAuth={true} requiredRoles={['other_ges_staff']}>
                <OtherGESStaffDashboard />
              </ProtectedRoute>
            } />
            <Route path="/document-generator" element={<DocumentGenerator />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/about" element={<About />} />
            <Route path="/dashboard" element={
              <ProtectedRoute requireAuth={true} requiredRoles={['headteacher', 'teacher']}>
                <DashboardSwitcher />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;